/**
 * 디자인 토큰을 TypeScript 에서 읽는 다리.
 *
 * 값의 출처는 언제나 `src/styles/globals.css` 다. 이 파일은 **이름만** 들고 다닌다 ,
 * 색을 하나 바꾸려고 CSS 와 TS 를 둘 다 고쳐야 한다면 그건 디자인 시스템이 아니다.
 *
 *   css(token)    브라우저가 CSS 로 읽는 자리 , inline style, SVG 의 fill/stroke.
 *                 `var(--color-stage-production)` 을 그대로 넘기면 된다. Recharts 의 fill 도 var() 를 읽는다.
 *   paint(token)  canvas 처럼 var() 를 모르는 자리 , 계산된 값을 실제로 읽어 온다.
 */

export type ColorToken =
  | "--color-canvas"
  | "--color-panel"
  | "--color-panel-soft"
  | "--color-line"
  | "--color-line-soft"
  | "--color-ink"
  | "--color-ink-soft"
  | "--color-ink-muted"
  | "--color-ink-subtle"
  | "--color-accent"
  | "--color-on-accent"
  | "--color-accent-deep"
  | "--color-accent-wash"
  | "--color-accent-bar"
  | "--color-tier-ample"
  | "--color-tier-moderate"
  | "--color-tier-tight"
  | "--color-tier-unknown"
  | "--color-tier-ample-fill"
  | "--color-tier-moderate-fill"
  | "--color-tier-tight-fill"
  | "--color-tier-unknown-fill"
  | "--color-on-tier-ample"
  | "--color-on-tier-moderate"
  | "--color-on-tier-tight"
  | "--color-on-tier-unknown"
  | "--color-surface-panel"
  | "--color-surface-inset"
  | "--color-surface-sunken"
  | "--color-stage-production"
  | "--color-stage-storage"
  | "--color-stage-transport"
  | "--color-stage-auxiliary"
  | "--color-on-stage"
  | "--color-stage-production-wash"
  | "--color-stage-storage-wash"
  | "--color-stage-transport-wash"
  | "--color-stage-auxiliary-wash"
  | "--color-boundary"
  | "--color-boundary-wash";

export type ShapeToken =
  | "--radius-badge"
  | "--radius-control"
  | "--radius-card"
  | "--radius-panel"
  | "--radius-pill"
  | "--shadow-panel"
  | "--shadow-popup";

export type TypeToken =
  | "--text-nano"
  | "--text-micro"
  | "--text-footnote"
  | "--text-caption"
  | "--text-label"
  | "--text-body"
  | "--text-lead"
  | "--font-sans"
  | "--font-mono";

export type Token = ColorToken | ShapeToken | TypeToken;

/**
 * CSS로 직접 그릴 수 없는 SVG,Recharts 기하의 타입 있는 토큰.
 * 값은 화면 컴포넌트에 흩어지지 않고 이 파일에서만 정의한다. 색과 달리 SVG viewBox,축 폭은
 * 브라우저 CSS 변수를 숫자로 받을 수 없으므로, 디자인 토큰의 예외적인 실행 다리로 둔다.
 */
export const CHART_LAYOUT = {
  equipmentRow: 28,
  equipmentHeightPad: 32,
  equipmentRightMargin: 56,
  equipmentLabelWidth: 150,
  equipmentBarMax: 20,
  equipmentBarRadius: 4,
  stageTopMargin: 20,
  stageRightMargin: 8,
  stageYAxisWidth: 48,
  stageBarMax: 40,
  stageBarRadius: 4,
  reportWidth: 640,
  reportRow: 22,
  reportLabelWidth: 200,
  reportValueWidth: 80,
  reportPad: 6,
  reportBarPad: 4,
  reportBarRadius: 2,
} as const;

/** CSS 위치 계산값이 필요한 공통 오버레이 기하. */
export const OVERLAY_LAYOUT = {
  tooltipEdge: 8,
  tooltipGap: 6,
  scrollThumbMin: 24,
} as const;

/** CSS 로 그대로 넘길 수 있는 참조 */
export function css(token: Token): string {
  return `var(${token})`;
}

/** 단계(생산,저장,이송,보조)별 색 토큰 , 카드 배지와 그래프가 같은 것을 쓴다 */
export const STAGE_COLOR = {
  production: { fill: "--color-stage-production", wash: "--color-stage-production-wash" },
  storage: { fill: "--color-stage-storage", wash: "--color-stage-storage-wash" },
  transport: { fill: "--color-stage-transport", wash: "--color-stage-transport-wash" },
  auxiliary: { fill: "--color-stage-auxiliary", wash: "--color-stage-auxiliary-wash" },
} as const satisfies Record<string, { fill: ColorToken; wash: ColorToken }>;

const painted = new Map<string, string>();

/** var() 를 모르는 자리(canvas)에 칠할 실제 값. 한 번 읽고 기억한다 */
export function paint(token: ColorToken): string {
  if (typeof document === "undefined") return "";
  const hit = painted.get(token);
  if (hit !== undefined) return hit;
  const value = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
  painted.set(token, value);
  return value;
}

/**
 * CSS 가 닿지 못하는 단 하나의 값 , index.html 의 <meta name="theme-color">.
 * ⚠️ globals.css 의 --color-canvas 와 같아야 한다. tests/design-tokens.test.ts 가 셋을 대조한다.
 */
/** CT for SC neutral-50. index.html의 theme-color와 globals.css의 canvas가 공유한다. */
export const STATIC_COLOR = { canvas: "#f9fafb" } as const;
